/**
 * Écriture — « **Le script qui tombe** » (⟦C52 · A-170 §1⟧, remplace le jeu de mémoire de ⟦C36⟧).
 *
 * Matt, 01/10/2026 : *« un truc à la Tetris ou Mario pilule : des mots qui tombent et il faut les
 * mettre au bon endroit. On ne cherche pas à éliminer les lignes, on cherche à mettre les mots au
 * bon endroit. Et ça va de plus en plus vite. »* Des blocs-phrases tombent un par un dans un plateau
 * de colonnes (Accroche, Histoire, Chute, Appel) ; on pousse chacun dans la bonne avant qu'il touche
 * le sol. Plus tard : des **pubs douteuses** (blocs rayés) à laisser filer, deux blocs à la fois.
 *
 * ## Qui décide de quoi
 *
 * - **Le serveur** génère la manche (la suite de blocs, leur colonne attendue, leurs vitesses) et
 *   la note, à partir du journal de placements que ce composant renvoie. Ici, **rien n'est tiré**.
 * - **`@maxime/shared`** porte la physique du plateau (`manche.ts` : où est chaque bloc, ce que font
 *   les touches) : le composant ne fait que la dessiner et lui passer les entrées. C'est la même
 *   physique que celle que le serveur suppose pour juger un placement plausible.
 *
 * ## Les gestes
 *
 * ← → déplacent le bloc d'une colonne ; ↓ (maintenu) le lâche, il tombe plus vite ; un appui (clic,
 * toucher) sur une colonne l'y envoie, un second appui sur la même le lâche. Trois boutons ◀ ▼ ▶
 * doublent ces gestes sous le plateau. Tout est jouable **au clavier** (flèches, ou Tab + Entrée sur
 * une colonne) **et au toucher**.
 *
 * Sous `prefers-reduced-motion`, la manche est plus lente (le serveur la génère telle) et les blocs
 * descendent par pas, jamais en glissade. Un lecteur d'écran lit chaque nouveau bloc et chaque
 * verdict par une zone `aria-live`.
 *
 * ⟦C57 · lot 8 · A-174⟧ **Moins de texte.** Consigne : une phrase, les touches dessinées avec un mot chacune,
 * un badge et une ligne par variante ; la règle entière, le geste au toucher, le score et la leçon de Matt
 * sont derrière « Les dessous ». Pendant la manche : aucun texte (un compteur, une jauge, un chrono).
 * Bilan : les nombres, et les ratés sur une ligne (« → Chute (posé : Histoire) »).
 */

import { useEffect, useMemo, useRef, useState, type CSSProperties } from "react";

import {
  LIBELLE_COLONNE,
  allerEnVoie,
  avancerManche,
  blocActif,
  creerManche,
  deplacerBloc,
  lacherBloc,
  type EvenementManche,
  type Manche,
} from "@maxime/shared";

import { DessousBloc, DessousLignes, LesDessous } from "../../components/LesDessous";
import { useSfx } from "../../components/SoundProvider";
import { assists as assistsDe } from "../../game/moteur";
import { useUIStore } from "../../stores/uiStore";
import { BlocChiffresDuTour } from "../core/BlocChiffresDuTour";
import { DessousMiniJeu } from "../core/DessousMiniJeu";
import { ligneExigence } from "../core/exigence";
import { NoteSur100 } from "../core/NoteSur100";
import { COMMANDES, CONSIGNE, ligneRateEcriture } from "../core/textes";
import { useChronoSonore } from "../core/useChronoSonore";
import { useRafLoop } from "../core/useRafLoop";
import { useTremblementSM } from "../core/useTremblementSM";
import type { ProprietesMiniJeu } from "../core/types";
import {
  DERNIER_TIERS,
  chaleurDeLaManche,
  depuisServeur,
  genererLocal,
  noterScript,
  type PatternScript,
  type ResultatScript,
} from "./pattern";

type Phase = "pret" | "jeu" | "fini";

/** Un bloc posé reste affiché ce temps-là au sol (flash vert ou rouge) avant de disparaître. */
const SEJOUR_AU_SOL_MS = 520;
/**
 * L'écran de fin nomme les premiers ratés, pas tous : le bouton « Suivant » reste à portée.
 * ⟦C57 · lot 8⟧ Trois, pas cinq : la liste ENTIÈRE (chaque raté, avec la colonne attendue et celle où le bloc
 * a été posé) est derrière « Les dessous » du bilan, jamais perdue.
 */
const RATES_AFFICHES = 3;
/** Mode posé : les blocs descendent par pas, jamais en glissade (§7.4). */
const PAS_DE_DESCENTE_POSE = 8;
/** ⟦C56 · A-173⟧ Les cases de la jauge de tension, et ses paliers de couleur (violet, or, rouge). */
const CASES_DE_TENSION = 8;

export function ScriptQuiTombe({
  stage,
  state,
  format,
  seed,
  pattern: servi,
  onFini,
  accueil,
  actionsPlus,
  consequence,
}: ProprietesMiniJeu) {
  const reducedMotion = useUIStore((s) => s.reducedMotion);
  /** ⟦A-76⟧ SPEC-UI §7.5 : en zone rouge de SM, la zone de jeu tremble (±1 px / 280 ms). */
  const tremblement = useTremblementSM(state);
  const sfx = useSfx();

  const pattern: PatternScript = useMemo(
    () =>
      servi
        ? depuisServeur(servi.patterns.ecriture, state)
        : genererLocal({
            state,
            format,
            seed,
            pose: reducedMotion,
            assists: assistsDe(state, "fil_rouge"),
          }),
    [servi, state, format, seed, reducedMotion],
  );
  const nVoies = pattern.colonnes.length;

  const [phase, setPhase] = useState<Phase>("pret");
  const manche = useRef<Manche | null>(null);
  /** Le temps de jeu du dernier pas, pour en tirer le delta de la frame. */
  const dernierTemps = useRef(0);
  const [, redessiner] = useState(0);
  const [annonce, setAnnonce] = useState("");
  const [resultat, setResultat] = useState<{ r: ResultatScript; placements: Manche["placements"] } | null>(null);
  const serie = useRef(0);
  const bien = useRef(0);

  function demarrer() {
    manche.current = creerManche(pattern.blocs, pattern.colonnes);
    dernierTemps.current = 0;
    serie.current = 0;
    bien.current = 0;
    setPhase("jeu");
  }

  /** Le son et l'annonce d'un événement : le moteur ne joue rien, il le dit. */
  function traiter(evts: EvenementManche[]) {
    for (const e of evts) {
      const b = pattern.blocs[e.bloc]!;
      if (e.type === "apparition") {
        // Un lecteur d'écran n'a que cette phrase : le texte du bloc ET la colonne où il tombe.
        setAnnonce(`Nouveau bloc : ${b.texte}. Il tombe en colonne ${LIBELLE_COLONNE[pattern.colonnes[b.voie]!]}.`);
      } else if (e.type === "pose_ok" || e.type === "piege_evite") {
        bien.current++;
        serie.current++;
        // Un petit son 8-bit au bon placement ; la série de cinq se fait entendre.
        sfx(e.type === "piege_evite" ? "cursor_stop" : serie.current % 5 === 0 ? "cut_perfect" : "cut_hit");
        setAnnonce(e.type === "piege_evite" ? "Pub laissée filer, bien joué" : "Bien rangé");
      } else {
        serie.current = 0;
        sfx(e.type === "piege_touche" ? "take_miss" : "cut_miss");
        const attendue = b.colonne ? LIBELLE_COLONNE[b.colonne] : null;
        setAnnonce(attendue ? `Raté : c'était ${attendue}` : "Raté : une pub ne se range pas");
      }
    }
  }

  function terminer() {
    const m = manche.current!;
    const placements = [...m.placements];
    setResultat({ r: noterScript(pattern, placements), placements });
    setPhase("fini");
  }

  useRafLoop(phase === "jeu", (ecoule) => {
    const m = manche.current;
    if (!m) return;
    const dt = ecoule - dernierTemps.current;
    dernierTemps.current = ecoule;
    traiter(avancerManche(m, dt));
    if (m.tMs >= pattern.dureeMs) terminer();
    else redessiner((n) => n + 1);
  });

  const restantMs = manche.current ? Math.max(0, pattern.dureeMs - manche.current.tMs) : pattern.dureeMs;
  useChronoSonore(restantMs, phase === "jeu");

  // — Le clavier : ← → ↓. Les flèches ne font défiler ni la page ni la zone de jeu. —
  useEffect(() => {
    if (phase !== "jeu") return;
    const bas = (e: KeyboardEvent) => {
      const m = manche.current;
      if (!m || e.ctrlKey || e.metaKey || e.altKey) return;
      if (e.key === "ArrowLeft" || e.key === "ArrowRight") {
        e.preventDefault();
        if (!e.repeat) deplacerBloc(m, e.key === "ArrowLeft" ? -1 : 1);
      } else if (e.key === "ArrowDown") {
        e.preventDefault();
        if (!e.repeat) lacherBloc(m, true);
      }
    };
    const haut = (e: KeyboardEvent) => {
      if (e.key === "ArrowDown" && manche.current) lacherBloc(manche.current, false);
    };
    window.addEventListener("keydown", bas);
    window.addEventListener("keyup", haut);
    return () => {
      window.removeEventListener("keydown", bas);
      window.removeEventListener("keyup", haut);
    };
  }, [phase]);

  // ─────────────────────────────── L'intro ───────────────────────────────

  if (phase === "pret") {
    return (
      <div className="jeu jeu-script">
        <h3 className="jeu-titre">✍️ Le script qui tombe</h3>
        {/* ⟦C62 · friction 3⟧ La 1re fois, la réplique de Matt tient la place de la phrase de règle : un seul écran. */}
        {accueil ?? <p className="jeu-regle">{CONSIGNE.ecriture.regle}</p>}
        <p className="jeu-regle jeu-commandes">
          <span className="touche-clavier">←</span> <span className="touche-clavier">→</span>{" "}
          {COMMANDES.ecriture.deplacer} · <span className="touche-clavier">↓</span>{" "}
          {COMMANDES.ecriture.lacher}
        </p>
        {pattern.pieges > 0 ? (
          <p className="jeu-variante" role="note">
            <span className="jeu-variante-badge">Pubs douteuses</span> {CONSIGNE.ecriture.piege}
          </p>
        ) : null}
        {pattern.doublets > 0 ? (
          <p className="jeu-variante" role="note">
            <span className="jeu-variante-badge">Deux à la fois</span> {CONSIGNE.ecriture.doublet}
          </p>
        ) : null}
        <ul className="jeu-params">
          <li>
            <strong>{pattern.blocs.length}</strong> blocs
          </li>
          <li>
            <strong>{nVoies}</strong> colonnes
          </li>
          <li>
            <strong>{Math.round(pattern.dureeMs / 1000)}</strong> s
          </li>
          {/* ⟦C62 · friction 7⟧ « exigence E0 » est derrière « Les dessous » (« Les chiffres du tour »). */}
          {pattern.erreursPardonnees > 0 ? (
            <li>
              <strong>{pattern.erreursPardonnees}</strong> erreur{pattern.erreursPardonnees > 1 ? "s" : ""}{" "}
              pardonnée{pattern.erreursPardonnees > 1 ? "s" : ""}
            </li>
          ) : null}
        </ul>
        <div className="jeu-actions">
          {/* ⟦C62 · friction 3⟧ Un seul libellé pour lancer un mini-jeu : « Jouer ». */}
          <button className="bouton primaire grand" type="button" onClick={demarrer}>
            Jouer
          </button>
          {actionsPlus}
          {/* ⟦C57 · lot 8⟧ Les phrases d'avant, au mot près : la règle, le geste au toucher, le score, puis la
              leçon de Matt entière (`DessousMiniJeu`). */}
          <DessousMiniJeu stage={stage}>
            <DessousBloc titre="La règle">
              <p className="dessous-texte">
                Les phrases de ton script tombent une par une. Pousse chacune dans la bonne colonne
                avant qu'elle touche le sol : ça va de plus en plus vite.
              </p>
              <p className="dessous-texte">
                <span className="touche-clavier">←</span> <span className="touche-clavier">→</span>{" "}
                déplacent le bloc, <span className="touche-clavier">↓</span> le lâche. Au toucher : une
                colonne pour y envoyer le bloc, la même encore pour le lâcher.
              </p>
              <p className="dessous-texte">Ton score est la part des blocs bien rangés, sur 100.</p>
            </DessousBloc>
            <BlocChiffresDuTour lignes={[ligneExigence(pattern.k)]} />
            {pattern.pieges > 0 ? (
              <DessousBloc titre="Pubs douteuses">
                <p className="dessous-texte">Un bloc rayé ne se range nulle part : laisse-le filer.</p>
              </DessousBloc>
            ) : null}
            {pattern.doublets > 0 ? (
              <DessousBloc titre="Deux à la fois">
                <p className="dessous-texte">
                  Parfois deux blocs tombent ensemble : tu déplaces toujours le plus bas.
                </p>
              </DessousBloc>
            ) : null}
          </DessousMiniJeu>
        </div>
      </div>
    );
  }

  // ─────────────────────────────── La fin ────────────────────────────────

  if (phase === "fini" && resultat) {
    const { r, placements } = resultat;
    const rates = r.sorts.filter((s) => !s.ok);
    return (
      // `data-placements` : le journal que ce jeu va renvoyer au serveur, lisible par un harnais de test.
      <div className="jeu jeu-script" data-placements={JSON.stringify(placements)}>
        <h3 className="jeu-titre">✍️ Script bouclé.</h3>
        <NoteSur100 note={r.scoreAffiche} consequence={consequence?.(r.scoreAffiche)} />
        <ul className="jeu-params">
          <li>
            <strong>
              {r.bonnes}/{r.total}
            </strong>{" "}
            blocs bien rangés
          </li>
          {r.pieges > 0 ? (
            <li>
              <strong>
                {r.piegesEvites}/{r.pieges}
              </strong>{" "}
              pub{r.pieges > 1 ? "s" : ""} laissée{r.pieges > 1 ? "s" : ""} filer
            </li>
          ) : null}
          {r.pardons > 0 ? <li>Plume d'or : {r.pardons > 1 ? `${r.pardons} erreurs pardonnées` : "une erreur pardonnée"}</li> : null}
        </ul>
        {rates.length > 0 ? (
          <ul className="script-final">
            {rates.slice(0, RATES_AFFICHES).map((s) => {
              const b = pattern.blocs[s.bloc]!;
              return (
                <li key={s.bloc} className="faux">
                  ✘ « {b.texte} »{" "}
                  <span className="script-attendu">
                    {ligneRateEcriture(
                      s.attendue ? LIBELLE_COLONNE[s.attendue] : null,
                      s.touche,
                      LIBELLE_COLONNE[s.posee],
                    )}
                  </span>
                </li>
              );
            })}
            {rates.length > RATES_AFFICHES ? (
              <li className="faux">
                … et {rates.length - RATES_AFFICHES} autre{rates.length - RATES_AFFICHES > 1 ? "s" : ""}
              </li>
            ) : null}
          </ul>
        ) : (
          <p className="jeu-regle">Sans faute !</p>
        )}
        <div className="jeu-actions">
          <button
            className="bouton primaire grand"
            type="button"
            onClick={() => onFini(r.scoreAffiche, { placements })}
          >
            Suivant
          </button>
          {rates.length > RATES_AFFICHES ? (
            <LesDessous titre="Script bouclé : tes ratés">
              <DessousBloc titre="Tous tes ratés">
                <DessousLignes
                  lignes={rates.map((s) => ({
                    icone: "✘",
                    texte: `« ${pattern.blocs[s.bloc]!.texte} »${
                      s.attendue
                        ? `, c'était ${LIBELLE_COLONNE[s.attendue]}${s.touche ? ` (posé en ${LIBELLE_COLONNE[s.posee]})` : ", tombé sans être posé"}`
                        : ", une pub se laisse filer"
                    }`,
                  }))}
                />
              </DessousBloc>
            </LesDessous>
          ) : null}
        </div>
      </div>
    );
  }

  // ─────────────────────────────── La manche ─────────────────────────────

  const m = manche.current;
  const actif = m ? blocActif(m) : null;
  const voieActive = m && actif !== null ? m.enJeu[actif]!.voie : null;
  // ⟦C56 · A-173⟧ Le signal de montée, sans texte : la chaleur (0 à 1) se lit des blocs servis, et
  // chauffe la jauge, le liseré du puits et, au dernier tiers, le fait trembler (jamais en mode posé).
  const chaleur = m ? chaleurDeLaManche(pattern.blocs, m.tMs) : 0;
  const casesAllumees = Math.round(chaleur * CASES_DE_TENSION);
  const frenetique = !reducedMotion && !!m && m.tMs >= pattern.dureeMs * DERNIER_TIERS;

  const toucher = (voie: number) => {
    if (manche.current) allerEnVoie(manche.current, voie);
  };

  return (
    <div className="jeu jeu-script jeu-manche" style={tremblement}>
      <div className="montage-entete">
        <span className="montage-compteur">
          ✓ {bien.current}/{pattern.blocs.length}
        </span>
        {/* ⟦C56⟧ La jauge de tension : pas un mot, des cases qui s'allument (violet, or, rouge) à mesure
            que la chute accélère. Décorative : le lecteur d'écran a déjà chaque bloc et chaque verdict. */}
        <span className="script-tension" aria-hidden="true" data-cases={casesAllumees}>
          {Array.from({ length: CASES_DE_TENSION }, (_, i) => (
            <i key={i} className={i < casesAllumees ? "allumee" : ""} />
          ))}
        </span>
        <span className="montage-restant">{(restantMs / 1000).toFixed(0)} s</span>
      </div>

      <div
        className={`chute ${frenetique ? "frenetique" : ""}`}
        style={{ "--voies": nVoies, "--chaleur": casesAllumees / CASES_DE_TENSION } as CSSProperties}
      >
        <div className="chute-colonnes" role="group" aria-label="Colonnes du script">
          {pattern.colonnes.map((c, i) => (
            <button
              key={c}
              type="button"
              className={`chute-colonne col-${c} ${voieActive === i ? "visee" : ""}`}
              aria-label={`Colonne ${LIBELLE_COLONNE[c]}`}
              onPointerDown={(e) => {
                e.preventDefault();
                toucher(i);
              }}
              // Entrée ou Espace sur une colonne au clavier : un clic sans pointeur (`detail === 0`).
              onClick={(e) => {
                if (e.detail === 0) toucher(i);
              }}
            >
              <span className="chute-etiquette">{LIBELLE_COLONNE[c]}</span>
            </button>
          ))}
        </div>

        <div className="chute-blocs" aria-hidden="true">
          {m
            ? pattern.blocs.map((b) => {
                const e = m.enJeu[b.i]!;
                if (e.etat === "attente") return null;
                if (e.etat === "pose" && m.tMs - (e.poseAtMs ?? 0) > SEJOUR_AU_SOL_MS) return null;
                const progres = pattern.pose
                  ? Math.floor(e.progres * PAS_DE_DESCENTE_POSE) / PAS_DE_DESCENTE_POSE
                  : e.progres;
                const piege = b.colonne === null;
                const classes = [
                  "chute-bloc",
                  piege ? "piege" : "",
                  b.offert ? "offert" : "",
                  b.i === actif ? "actif" : "",
                  e.etat === "pose" ? (piege ? (e.ok ? "pose-evite" : "pose-rate") : e.ok ? "pose-ok" : "pose-rate") : "",
                ]
                  .filter(Boolean)
                  .join(" ");
                return (
                  <div
                    key={b.i}
                    className={classes}
                    data-bloc={b.i}
                    data-voie={e.voie}
                    data-progres={progres.toFixed(2)}
                    style={{
                      left: `${(e.voie / nVoies) * 100}%`,
                      width: `${100 / nVoies}%`,
                      top: `calc(${progres} * (100% - var(--bloc-h)))`,
                    }}
                  >
                    <div className="chute-bloc-carte">
                      {piege ? <span className="chute-etiquette-pub">PUB</span> : null}
                      {b.offert ? <span className="chute-etiquette-offert">offert</span> : null}
                      <span className="chute-bloc-texte">{b.texte}</span>
                    </div>
                  </div>
                );
              })
            : null}
        </div>
      </div>

      <div className="chute-commandes">
        <button
          type="button"
          className="bouton"
          aria-label="Bloc d'une colonne à gauche"
          onPointerDown={(e) => {
            e.preventDefault();
            if (manche.current) deplacerBloc(manche.current, -1);
          }}
        >
          ◀
        </button>
        <button
          type="button"
          className="bouton"
          aria-label="Lâcher le bloc"
          onPointerDown={(e) => {
            e.preventDefault();
            if (manche.current) lacherBloc(manche.current, true);
          }}
          onPointerUp={() => manche.current && lacherBloc(manche.current, false)}
          onPointerLeave={() => manche.current && lacherBloc(manche.current, false)}
          onPointerCancel={() => manche.current && lacherBloc(manche.current, false)}
        >
          ▼ Lâcher
        </button>
        <button
          type="button"
          className="bouton"
          aria-label="Bloc d'une colonne à droite"
          onPointerDown={(e) => {
            e.preventDefault();
            if (manche.current) deplacerBloc(manche.current, 1);
          }}
        >
          ▶
        </button>
      </div>

      <p className="chute-annonce" role="status" aria-live="polite">
        {annonce}
      </p>
    </div>
  );
}
